import { format } from 'date-fns';
import { AlertCircle, Loader2, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import manifestService, { Manifest } from '../../services/manifestService';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

interface TrackingTabProps {
  manifest: Manifest;
  onUpdate: () => void;
  disabled?: boolean;
}

const TRACKING_EVENT_OPTIONS = [
  'SHIPMENT MANIFESTED',
  'SHIPMENT PICKED UP',
  'ARRIVED AT ORIGIN HUB',
  'DEPARTED ORIGIN HUB',
  'IN TRANSIT',
  'ARRIVED AT DESTINATION HUB',
  'OUT FOR DELIVERY',
  'DELIVERED',
  'CUSTOMS CLEARANCE',
  'HELD AT CUSTOMS',
  'RELEASED FROM CUSTOMS',
  'RETURNED TO SENDER',
  'DELIVERY ATTEMPTED',
  'EXCEPTION',
];

const TrackingTab: React.FC<TrackingTabProps> = ({
  manifest,
  onUpdate,
  disabled = false,
}) => {
  const [eventDateTime, setEventDateTime] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { toast } = useToast();

  // Set default date/time to now
  useEffect(() => {
    const now = new Date();
    const localDatetime = new Date(
      now.getTime() - now.getTimezoneOffset() * 60000
    )
      .toISOString()
      .slice(0, 16);
    setEventDateTime(localDatetime);
  }, []);

  const handleSubmit = async () => {
    if (!eventDateTime || !eventDescription || !eventLocation) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      await manifestService.addTrackingEvent(manifest._id, {
        eventDateTime,
        eventDescription,
        eventLocation,
        applyToAwbs: true,
      });

      // Reset form
      const now = new Date();
      const localDatetime = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      )
        .toISOString()
        .slice(0, 16);
      setEventDateTime(localDatetime);
      setEventDescription('');
      setEventLocation('');

      toast({
        title: 'Success',
        description: 'Tracking event added to all AWBs',
      });

      onUpdate();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add tracking event',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (eventId: string) => {
    setDeletingId(eventId);
    try {
      await manifestService.deleteTrackingEvent(manifest._id, eventId);
      toast({
        title: 'Success',
        description: 'Tracking event deleted',
      });
      onUpdate();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete tracking event',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className='space-y-6'>
      {/* Add Tracking Info Header */}
      <div className='bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4'>
        <h3 className='text-base sm:text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-4'>
          ADD TRACKING INFO TO ALL AWBS OF THIS MANIFEST NO.{' '}
          {manifest.manifestNo}
        </h3>

        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
          <div>
            <Label
              htmlFor='eventDateTime'
              className='text-yellow-800 dark:text-yellow-200'
            >
              EVENT DATE TIME
            </Label>
            <Input
              id='eventDateTime'
              type='datetime-local'
              value={eventDateTime}
              onChange={e => setEventDateTime(e.target.value)}
              disabled={disabled}
              className='mt-1'
            />
          </div>

          <div>
            <Label
              htmlFor='eventDescription'
              className='text-yellow-800 dark:text-yellow-200'
            >
              EVENT DESCRIPTION
            </Label>
            <Select
              value={eventDescription}
              onValueChange={setEventDescription}
              disabled={disabled}
            >
              <SelectTrigger className='mt-1'>
                <SelectValue placeholder='Select...' />
              </SelectTrigger>
              <SelectContent>
                {TRACKING_EVENT_OPTIONS.map(option => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label
              htmlFor='eventLocation'
              className='text-yellow-800 dark:text-yellow-200'
            >
              EVENT LOCATION
            </Label>
            <Input
              id='eventLocation'
              placeholder='Enter location...'
              value={eventLocation}
              onChange={e => setEventLocation(e.target.value)}
              disabled={disabled}
              className='mt-1'
            />
          </div>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={disabled || loading}
          className='mt-4 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto'
        >
          {loading ? (
            <Loader2 className='h-4 w-4 animate-spin mr-2' />
          ) : (
            <Plus className='h-4 w-4 mr-2' />
          )}
          SUBMIT
        </Button>
      </div>

      {/* Events Added Section */}
      <div>
        <h3 className='text-lg font-semibold mb-4'>EVENTS ADDED</h3>

        {manifest.trackingEvents.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-8 text-muted-foreground border rounded-lg'>
            <AlertCircle className='h-8 w-8 mb-2' />
            <p>No tracking events added yet</p>
          </div>
        ) : (
          <div className='overflow-x-auto -mx-4 sm:mx-0'>
            <div className='min-w-[600px] sm:min-w-0 px-4 sm:px-0'>
              <Table>
                <TableHeader>
                  <TableRow className='bg-muted/50'>
                    <TableHead>EVENT DATE TIME</TableHead>
                    <TableHead>EVENT DESCRIPTION</TableHead>
                    <TableHead>EVENT LOCATION</TableHead>
                    <TableHead>CREATED DATE</TableHead>
                    <TableHead>ACTION</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manifest.trackingEvents.map(event => (
                    <TableRow key={event._id}>
                      <TableCell>
                        {format(
                          new Date(event.eventDateTime),
                          'dd/MM/yyyy, hh:mm:ss a'
                        )}
                      </TableCell>
                      <TableCell>{event.eventDescription}</TableCell>
                      <TableCell>{event.eventLocation}</TableCell>
                      <TableCell>
                        {format(
                          new Date(event.createdDate),
                          'dd/MM/yyyy, hh:mm:ss a'
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant='destructive'
                          size='sm'
                          onClick={() => handleDelete(event._id)}
                          disabled={disabled || deletingId === event._id}
                        >
                          {deletingId === event._id ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            <>
                              <Trash2 className='h-4 w-4 mr-1' />
                              DELETE
                            </>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingTab;
